import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShoppingBag } from "lucide-react";
import publicApi from "@/lib/publicApi";
import { storeTrackingSearch } from "@/lib/storeCart";
import { PublicStoreShell, StoreLogo } from "@/components/store/PublicStoreShell";

export default function StoreHome() {
  const { branchCode } = useParams();
  const [searchParams] = useSearchParams();
  const qs = storeTrackingSearch(searchParams);
  const isCall = searchParams.get("source") === "call";

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-store", branchCode],
    queryFn: async () => {
      const { data: res } = await publicApi.get(`/public/store/${branchCode}`);
      return res;
    },
    enabled: Boolean(branchCode),
  });

  if (isLoading) {
    return (
      <PublicStoreShell>
        <div className="flex min-h-[100dvh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin store-primary-text" />
        </div>
      </PublicStoreShell>
    );
  }

  if (error) {
    return (
      <PublicStoreShell>
        <div className="flex min-h-[100dvh] items-center justify-center px-6 text-center text-sm text-red-600">
          {error.response?.data?.message || "Store unavailable"}
        </div>
      </PublicStoreShell>
    );
  }

  const store = data?.store ?? {};
  const catalogUrl = `/s/${branchCode}/catalog${qs}`;

  return (
    <PublicStoreShell>
      <div className="flex min-h-[100dvh] flex-col">
        <div className="relative mx-3 mt-3 min-h-[58dvh] overflow-hidden rounded-[36px] shadow-xl">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(145deg, #2B0B04 0%, #4A1810 40%, #F36F21 100%)",
            }}
          />
          <div className="relative z-10 flex min-h-[58dvh] flex-col items-center justify-center px-6 py-10 text-center text-white">
            <StoreLogo name={store.hero_title || store.name} className="text-3xl mb-4" />
            <h1 className="text-2xl font-extrabold tracking-tight">{store.hero_title || store.name}</h1>
            <p className="mt-2 text-sm text-white/75 max-w-xs">
              {store.hero_subtitle || "Order online · Pickup or delivery"}
            </p>
            {isCall && (
              <span className="mt-4 rounded-full bg-amber-400/20 border border-amber-300/40 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-100">
                Call order mode
              </span>
            )}
          </div>
        </div>

        <div className="px-5 pt-6 pb-8 space-y-4">
          {(store.phone || store.address) && (
            <div className="rounded-2xl border border-[#E8D5C8] bg-white p-4 text-sm shadow-sm">
              {store.phone && <p className="font-medium text-[#2B0B04]">{store.phone}</p>}
              {store.address && <p className="mt-1 text-[#8B6B5E] text-[13px]">{store.address}</p>}
            </div>
          )}
          <Link
            to={catalogUrl}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#F36F21] text-white font-bold shadow-lg shadow-orange-200"
          >
            <ShoppingBag size={18} />
            View menu & order
          </Link>
        </div>
      </div>
    </PublicStoreShell>
  );
}
